import { useState, useEffect, useId, type FormEvent } from 'react';
import { Modal } from '@/components/ui/Modal/Modal';
import { Button } from '@/components/ui/Button/Button';
import { Input } from '@/components/ui/Input/Input';
import { PROJECT_FILES_MAX_FILENAME_LENGTH } from './projectFilesPopoverConstants';
import './ProjectFileRenameModal.css';

export interface ProjectFileRenameModalProps {
  open: boolean;
  onClose: () => void;
  /** Редактируемая часть без расширения */
  initialStem: string;
  /** Включая ведущую точку (.pdf и т.д.); не редактируется */
  immutableSuffix: string;
  submitting?: boolean;
  onSave: (fullFileName: string) => Promise<void>;
}

export function ProjectFileRenameModal({
  open,
  onClose,
  initialStem,
  immutableSuffix,
  submitting = false,
  onSave,
}: ProjectFileRenameModalProps) {
  const formDomId = `project-file-rename-${useId().replace(/:/g, '')}`;
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | undefined>();

  const stemMaxChars = Math.max(0, PROJECT_FILES_MAX_FILENAME_LENGTH - immutableSuffix.length);

  useEffect(() => {
    if (!open) return;
    setValue(initialStem);
    setError(undefined);
  }, [open, initialStem]);

  const performSave = async () => {
    const trimmedStem = value.trim();
    if (!trimmedStem) {
      setError('Укажите название файла');
      return;
    }
    const fullName = trimmedStem + immutableSuffix;
    if (fullName.length > PROJECT_FILES_MAX_FILENAME_LENGTH) {
      setError(`Имя файла не более ${PROJECT_FILES_MAX_FILENAME_LENGTH} символов`);
      return;
    }
    if (trimmedStem === initialStem.trim()) {
      onClose();
      return;
    }

    setError(undefined);
    try {
      await onSave(fullName);
      onClose();
    } catch {
      /* Сообщение об ошибке — в мутации */
    }
  };

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault();
    void performSave();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Название файла"
      headerVariant="island"
      size="sm"
      overlayClassName="project-file-rename-modal-overlay"
    >
      <form id={formDomId} onSubmit={handleFormSubmit} className="form">
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value.slice(0, stemMaxChars))}
          fullWidth
          autoFocus
          disabled={submitting}
          error={error}
          maxLength={stemMaxChars > 0 ? stemMaxChars : undefined}
          spellCheck={false}
          placeholder="Имя файла"
          aria-label="Имя файла"
        />
        <div className="form__footer form__footer_modal">
          <Button variant="outlined" type="button" onClick={onClose} disabled={submitting}>
            Отмена
          </Button>
          {/* type="button": не участвует в submit внешней формы «Добавить проект», когда попап файлов внутри неё в дереве React */}
          <Button type="button" variant="primary" disabled={submitting} onClick={() => void performSave()}>
            {submitting ? 'Сохранение…' : 'Сохранить'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
